// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import { Currency, type CurrencyWireFormat, NewCurrency } from '../Currency/Currency';
import Enums from '../Enums';
import {
  type IntegrationSummary,
  type IntegrationSummaryWireFormat,
  NewIntegrationSummary,
} from '../IntegrationSummary/IntegrationSummary';
import {
  type MixMaterial,
  type MixMaterialWireFormat,
  NewMixMaterial,
} from '../MixMaterial/MixMaterial';
import { NewPlant, Plant, type PlantWireFormat } from '../Plant/Plant';

export class Mix {
  readonly id: string;
  readonly alternateID: string;
  readonly name: string;
  readonly strength: number;
  readonly strengthUnit: Enums.Unit;
  readonly mixMaterials: MixMaterial[];
  readonly materialCostOverride: Currency | null;
  readonly plant: Plant;
  readonly externalID: string | null;
  readonly slump: string | null;
  readonly aggregateSize: string | null;
  readonly waterCementRatio: string | null;
  readonly flyAshPercentage: string | null;
  readonly category: Enums.MixCategory;
  readonly isArchived: boolean;
  readonly divisionId: string | null;
  readonly integrationSummary: IntegrationSummary | null;
  readonly quantityUnit: Enums.Unit;
  readonly materialCost: Currency;
  readonly incompatibleQuantityUnits: boolean;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.alternateID = data.alternateID === undefined ? '' : data.alternateID;
    this.name = data.name === undefined ? '' : data.name;
    this.strength = data.strength === undefined ? 0 : data.strength;
    this.strengthUnit = data.strengthUnit === undefined ? Enums.Unit.Psi : data.strengthUnit;
    this.mixMaterials = (data.mixMaterials ?? []).map((mixMaterials: any) =>
      NewMixMaterial(mixMaterials),
    );
    this.materialCostOverride =
      (data.materialCostOverride ?? null) === null ? null : NewCurrency(data.materialCostOverride);
    this.plant = NewPlant(data.plant);
    this.externalID =
      // eslint-disable-next-line no-nested-ternary
      (data.externalID ?? null) === null
        ? null
        : data.externalID === undefined
          ? ''
          : data.externalID;
    this.slump = data.slump === undefined ? null : data.slump;
    this.aggregateSize = data.aggregateSize === undefined ? null : data.aggregateSize;
    this.waterCementRatio = data.waterCementRatio === undefined ? null : data.waterCementRatio;
    this.flyAshPercentage = data.flyAshPercentage === undefined ? null : data.flyAshPercentage;
    this.category = data.category === undefined ? Enums.MixCategory.ReadyMix : data.category;
    this.isArchived = data.isArchived === undefined ? false : data.isArchived;
    this.divisionId = data.divisionId === undefined ? null : data.divisionId;
    this.integrationSummary =
      // eslint-disable-next-line no-nested-ternary
      (data.integration ?? data.integrationSummary ?? null) === null
        ? null
        : NewIntegrationSummary(data.integration ?? data.integrationSummary);
    this.quantityUnit = data.quantityUnit === undefined ? Enums.Unit.CuYd : data.quantityUnit;
    this.materialCost = NewCurrency(data.materialCost);
    this.incompatibleQuantityUnits =
      (data.incompatibleBatchUnits ?? data.incompatibleQuantityUnits) === undefined
        ? false
        : (data.incompatibleBatchUnits ?? data.incompatibleQuantityUnits);
  }

  static zero(): Mix {
    const zeroValues: ExcludeMethodsDeep<Mix> = {
      id: NIL_UUID,
      alternateID: '',
      name: '',
      strength: 0,
      strengthUnit: Enums.Unit.Psi,
      mixMaterials: [],
      materialCostOverride: null,
      plant: Plant.zero(),
      externalID: null,
      slump: null,
      aggregateSize: null,
      waterCementRatio: null,
      flyAshPercentage: null,
      category: Enums.MixCategory.ReadyMix,
      isArchived: false,
      divisionId: null,
      integrationSummary: null,
      quantityUnit: Enums.Unit.CuYd,
      materialCost: Currency.zero(),
      incompatibleQuantityUnits: false,
    };
    return new Mix(zeroValues);
  }

  toJSON(): { [key: string]: any } {
    return {
      id: this.id,
      alternateID: this.alternateID,
      name: this.name,
      strength: this.strength,
      strengthUnit: this.strengthUnit,
      mixMaterials: this.mixMaterials,
      materialCostOverride: this.materialCostOverride,
      plant: this.plant,
      externalID: this.externalID,
      slump: this.slump,
      aggregateSize: this.aggregateSize,
      waterCementRatio: this.waterCementRatio,
      flyAshPercentage: this.flyAshPercentage,
      category: this.category,
      isArchived: this.isArchived,
      divisionId: this.divisionId,
      integration: this.integrationSummary,
      quantityUnit: this.quantityUnit,
      materialCost: this.materialCost,
      incompatibleBatchUnits: this.incompatibleQuantityUnits,
    };
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type MixWireFormat = {
  id: string;
  alternateID: string;
  name: string;
  strength: number;
  strengthUnit: string;
  mixMaterials: MixMaterialWireFormat[];
  materialCostOverride: CurrencyWireFormat | null;
  plant: PlantWireFormat;
  externalID: string | null;
  slump: string | null;
  aggregateSize: string | null;
  waterCementRatio: string | null;
  flyAshPercentage: string | null;
  category: string;
  isArchived: boolean;
  divisionId: string | null;
  integrationSummary: IntegrationSummaryWireFormat | null;
  quantityUnit: string;
  materialCost: CurrencyWireFormat;
  incompatibleQuantityUnits: boolean;
};

type NewMixProps = PartialDeep<Mix, { recurseIntoArrays: true }>;
export const NewMix: (props: NewMixProps) => Mix = (props) => new Mix(props);

type NewMixFromWireFormatProps = PartialDeep<MixWireFormat, { recurseIntoArrays: true }>;
export const NewMixFromWireFormat: (props: NewMixFromWireFormatProps) => Mix = (props) =>
  new Mix(props);
